import { zodResolver } from '@hookform/resolvers/zod';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { KeyRound } from 'lucide-react';
import { useForm, useWatch } from 'react-hook-form';
import type { z } from 'zod';

import {
  AuthError,
  AuthHeading,
  AuthPasswordField,
  AuthSubmit,
  getAuthSession,
  newPasswordSchema,
  PasswordChecklist,
  useAuth,
  useAuthNavigation,
} from '@/app/auth';

export const Route = createFileRoute('/login/confirmar-senha')({
  beforeLoad: () => {
    const state = getAuthSession().getState();
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- contrato do TanStack Router
    if (state.status !== 'challenge' || state.challenge !== 'newPassword') throw redirect({ to: '/login' });
  },
  component: NewPasswordScreen,
});

type Values = z.infer<ReturnType<typeof newPasswordSchema>>;

/** Primeiro acesso: o Cognito exige senha nova (`NEW_PASSWORD_REQUIRED`). */
function NewPasswordScreen() {
  const { t } = useLingui();
  const { session, pending, error } = useAuth();
  const follow = useAuthNavigation(session);
  const form = useForm<Values>({ resolver: zodResolver(newPasswordSchema()), defaultValues: { password: '', passwordConfirmation: '' } });
  const password = useWatch({ control: form.control, name: 'password' });

  const onSubmit = form.handleSubmit(async ({ password: next }) => {
    await follow(await session.confirmNewPassword(next));
  });

  return (
    <form className="flex flex-col gap-7" noValidate onSubmit={(event) => void onSubmit(event)}>
      <AuthHeading icon={KeyRound} eyebrow={<Trans>Primeiro acesso</Trans>} title={<Trans>Cadastrar nova senha</Trans>}>
        <Trans>Para acessar o sistema, você deverá cadastrar uma nova senha</Trans>
      </AuthHeading>
      <AuthError message={error} />
      <div className="flex flex-col gap-4">
        <AuthPasswordField label={t`Senha`} autoComplete="new-password" registration={form.register('password')} error={form.formState.errors.password} />
        <PasswordChecklist value={password} />
        <AuthPasswordField
          label={t`Confirmar senha`}
          autoComplete="new-password"
          registration={form.register('passwordConfirmation')}
          error={form.formState.errors.passwordConfirmation}
        />
      </div>
      <AuthSubmit pending={pending}>
        <Trans>Confirmar</Trans>
      </AuthSubmit>
    </form>
  );
}
